import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { Button } from '../controls';

export default function FilterableTable(props) {
  const {
    addNewRecordText,
    allowAddNewRecord,
    includeFilter,
    tableTitle,
    toggleModal,
    editHandler,
    viewLinkBuilder,
    data = [],
    columns = {},
  } = props;

  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState(data);
  const [filteredRows, setFilteredRows] = useState(data);
  const [sort, setSort] = useState({
    key: null,
    order: 'desc',
  });
  const prevLenRef = useRef(data.length);

  useEffect(() => {
    setRows(data);
    setFilteredRows(data);
    prevLenRef.current = data.length;
  }, []);

  useEffect(() => {
    if (prevLenRef.current !== data.length) {
      prevLenRef.current = data.length;
      setRows(data);
      setFilteredRows(data);
    }
  }, [data, data.length]);

  const editRow = (event, row) => {
    event.preventDefault();
    editHandler(row);
  };

  const filterByText = (e) => {
    let updatedList = rows;
    const filterText = e.target.value.toLowerCase();

    if (filterText !== '') {
      const filterableKeys = Object.keys(columns).filter((key) => {
        const column = columns[key];
        return column.filterable;
      });
      updatedList = updatedList.filter((item) => {
        return filterableKeys.some((key) => matchesText(item[key], filterText));
      });
    } else {
      updatedList = rows;
    }

    setFilteredRows(updatedList);
    setFilter(filterText);
  };

  const matchesText = (value, filterText) => {
    if (!filterText || !filterText.length) {
      return true;
    }
    if (!value) {
      return false;
    }
    const tokens = filterText.split(' ');
    return tokens.every((token) => {
      return (
        value.toLocaleLowerCase().includes(token.toLocaleLowerCase()) ||
        value.toLocaleLowerCase().includes(token.toLocaleLowerCase())
      );
    });
  };

  const getSortIcon = (key, column) => {
    if (!column.sortable) {
      return null;
    }
    if (sort.key === key) {
      if (sort.order === 'asc') {
        return <i className="fa fa-lg fa-arrow-up" />;
      }
      return <i className="fa fa-lg fa-arrow-down" />;
    }
    return null;
  };

  const sortRows = (key) => {
    const newSortOrder = sort.order === 'asc' ? 'desc' : 'asc';

    const orderedFilteredRows = _.orderBy(filteredRows, key, newSortOrder);
    setSort((s) => ({
      ...s,
      order: newSortOrder,
      key,
    }));
    setFilteredRows(orderedFilteredRows);
  };

  return (
    <section className="filterable-table-wrapper">
      <header className="mb-8 flex flex-row flex-wrap items-center justify-between">
        <h1 className="order-1 mb-0 leading-none text-[#73797f]">
          {tableTitle}
        </h1>

        {includeFilter && (
          <div className="filter-records order-3 mx-0 mb-4 mt-0 w-full max-w-[400px]">
            <input
              type="text"
              id="filter-records"
              placeholder={`Search ${tableTitle}`}
              value={filter}
              onChange={filterByText}
              className="rounded-[1.25rem] outline-none"
            />
          </div>
        )}

        {allowAddNewRecord && (
          <Button className="order-2" onClick={() => toggleModal({})}>
            <FontAwesomeIcon icon={['far', 'plus']} /> Add {addNewRecordText}
          </Button>
        )}
      </header>
      <table className="table filterable-table w-full border-separate">
        <thead>
          <tr>
            {Object.keys(columns).map((key) => {
              const column = columns[key];
              return (
                <th
                  className="h-[30px] text-left text-base font-medium uppercase text-[#73797f]"
                  onClick={() => sortRows(key)}
                  key={key}
                >
                  {column.header} {getSortIcon(key, column)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row, index) => {
            const cells = [];
            Object.keys(columns).map((key) => {
              const column = columns[key];
              const value = row[key];
              if (column.showViewLink) {
                cells.push(
                  <td
                    className="h-[50px] overflow-hidden text-ellipsis whitespace-nowrap text-[var(--primary)]"
                    key={key}
                  >
                    <Link to={viewLinkBuilder(row)}>{value}</Link>
                  </td>,
                );
              } else if (column.handleClick) {
                cells.push(
                  <td
                    className="h-[50px] cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap text-[var(--primary)]"
                    key={key}
                    onClick={() => editHandler(row)}
                  >
                    {value}
                  </td>,
                );
              } else if (key === 'action_url') {
                if (editHandler) {
                  cells.push(
                    <td
                      className="h-[50px] overflow-hidden text-ellipsis whitespace-nowrap text-[var(--primary)]"
                      key={key}
                    >
                      <Link to="#" onClick={(e) => editRow(e, row)}>
                        <i className="fas fa-pencil-alt" />
                      </Link>
                    </td>,
                  );
                } else {
                  cells.push(
                    <td
                      className="h-[50px] overflow-hidden text-ellipsis whitespace-nowrap text-[var(--primary)]"
                      key={key}
                    >
                      <Link to={row[key]}>
                        <i className="fas fa-pencil-alt" />
                      </Link>
                    </td>,
                  );
                }
              } else {
                cells.push(
                  <td
                    className="h-[50px] overflow-hidden text-ellipsis whitespace-nowrap text-[var(--primary)]"
                    key={key + value}
                  >
                    {value}
                  </td>,
                );
              }
              return cells;
            });
            return <tr key={index}>{cells}</tr>;
          })}
        </tbody>
      </table>
    </section>
  );
}
